import { useCallback } from "react";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import {
  useDeleteProjectFileMutation,
  useGetProjectFileUploadUrlMutation,
  useRegisterProjectFileUploadMutation,
} from "@gql/sites-api/graphql";

export interface ProjectUploadCallbacks {
  onUpload: (file: File) => Promise<string>; // Returns UploadedFile ID
  onDelete: (fileId: string) => Promise<boolean>;
}

export function useProjectUploadCallbacks(): ProjectUploadCallbacks {
  const [getSignedUrl] = useGetProjectFileUploadUrlMutation({
    context: { endpoint: LandTechEndpoints.Sites },
    client: routedClient,
  });

  const [registerUpload] = useRegisterProjectFileUploadMutation({
    context: { endpoint: LandTechEndpoints.Sites },
    client: routedClient,
  });

  const [deleteFile] = useDeleteProjectFileMutation({
    context: { endpoint: LandTechEndpoints.Sites },
    client: routedClient,
  });

  const onUpload = useCallback(
    async (file: File): Promise<string> => {
      const { data: urlData, errors: urlErrors } = await getSignedUrl({
        variables: { filename: file.name, mimetype: file.type },
      });
      if (urlErrors?.length || !urlData?.getProjectFileUploadUrl) throw new Error("Failed to get signed URL");

      const { signedUrl, s3Key } = urlData.getProjectFileUploadUrl;
      const response = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!response.ok) throw new Error("S3 upload failed");

      const { data: regData, errors: regErrors } = await registerUpload({ variables: { s3Key } });
      if (regErrors?.length || !regData?.registerProjectFileUpload) throw new Error("Registration failed");

      return regData.registerProjectFileUpload;
    },
    [getSignedUrl, registerUpload]
  );

  const onDelete = useCallback(
    async (fileId: string): Promise<boolean> => {
      const { data, errors } = await deleteFile({ variables: { fileId } });
      if (errors?.length || !data?.deleteProjectFile) throw new Error("Delete failed");
      return data.deleteProjectFile;
    },
    [deleteFile]
  );

  return { onUpload, onDelete };
}
